import React, { useState, useEffect, useCallback } from 'react';
import api from '../config';
import {
  FolderOpen, Loader2, Link2, AlertCircle, ChevronDown, ChevronRight, ExternalLink,
  CheckCircle2, FileWarning,
} from 'lucide-react';

const DOMAIN_LABELS = {
  financials: 'Financials',
  hr: 'HR',
  billing: 'Billing / AR',
  crm: 'CRM',
  unmapped: 'Unfiled',
};

const DOMAIN_ORDER = ['financials', 'billing', 'crm', 'hr', 'unmapped'];

const STATUS_COPY = {
  pulled: 'Pulled',
  partial: 'Partly pulled',
  failed: 'Could not pull',
  gated: 'Not accessible',
  unreachable: 'Link not reachable',
  accessible_empty: 'Empty room',
};

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Pull a shared data room into this deal.
 *
 * Sellers send a Drive folder link; the room behind it is usually nested
 * several folders deep. Caply walks the tree with the firm's own Google
 * account, copies what it can read into the deal's Drive folder, and files
 * every document under the diligence domain its path points at. Anything it
 * cannot take is listed with the reason, never quietly dropped.
 */
export default function DataRoomPull({ dealId }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const { data } = await api.get(`/api/dd/deals/${dealId}/dataroom`);
      setRooms(data.rooms || []);
    } catch {
      setRooms([]);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    const link = url.trim();
    if (!link) return;
    setBusy(true); setError(''); setRows([]);
    try {
      const { data } = await api.post(`/api/dd/deals/${dealId}/dataroom/crawl`, { url: link });
      setRows(data.items || []);
      setOpen(true);
      setUrl('');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not pull that room right now.');
    } finally {
      setBusy(false);
    }
  };

  const fileCount = rooms.reduce((n, r) => n + (r.items?.length || 0), 0);

  return (
    <div className="card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-lg bg-brand-50 text-brand-700"><FolderOpen className="w-4 h-4" /></span>
          <div>
            <p className="font-semibold text-navy-900 text-sm">Data room</p>
            <p className="text-xs text-slate-500">
              {fileCount > 0
                ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'} pulled from ${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'}`
                : 'Paste the shared link a seller or banker sent and Caply pulls the files in'}
            </p>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') pull(); }}
                placeholder="https://drive.google.com/drive/folders/..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
            <button
              onClick={pull}
              disabled={busy || !url.trim()}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-navy-950 text-sm font-semibold hover:bg-brand-600 disabled:opacity-50 transition"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderOpen className="w-4 h-4" />}
              {busy ? 'Pulling…' : 'Pull files'}
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <p className="flex-1">{error}</p>
            </div>
          )}

          {rows.length > 0 && (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 flex items-center gap-2 text-xs text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>{rows.length} {rows.length === 1 ? 'file' : 'files'} copied into this deal</span>
              </div>
              {DOMAIN_ORDER.filter((d) => rows.some((r) => (r.domain || 'unmapped') === d)).map((d) => (
                <div key={d} className="border-t border-slate-100">
                  <p className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-400">
                    {DOMAIN_LABELS[d] || d}
                  </p>
                  {rows.filter((r) => (r.domain || 'unmapped') === d).map((r) => (
                    <div key={`${d}-${r.filename}-${r.path || ''}`} className="px-3 py-1.5 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="truncate text-navy-900">{r.filename}</p>
                        {r.path && r.path !== r.filename && (
                          <p className="truncate text-[11px] text-slate-400">{r.path}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
                        {fmtSize(r.size)}
                        {r.drive_link && (
                          <a href={r.drive_link} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline inline-flex items-center gap-1">
                            Open <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {rooms.map((room) => {
            const skipped = room.skipped || [];
            const errors = room.errors || [];
            const isOpen = expanded === room.id;
            return (
              <div key={room.id} className="rounded-lg border border-slate-200">
                <button
                  onClick={() => setExpanded(isOpen ? null : room.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-navy-900 truncate">{room.url}</p>
                    <p className="text-xs text-slate-500">
                      {STATUS_COPY[room.status] || room.status || 'Tracked'}
                      {' · '}
                      {(room.items || []).length} files
                      {skipped.length > 0 && ` · ${skipped.length} skipped`}
                      {errors.length > 0 && ` · ${errors.length} unreadable`}
                      {room.folders_seen ? ` · ${room.folders_seen} folders` : ''}
                    </p>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-100">
                    {(room.items || []).map((item) => (
                      <div key={`${room.id}-${item.filename}-${item.path || ''}`} className="px-3 py-1.5 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <p className="truncate text-navy-900">{item.filename}</p>
                          <p className="text-[11px] text-slate-400">
                            {DOMAIN_LABELS[item.domain] || item.domain}
                            {item.path && item.path !== item.filename ? ` · ${item.path}` : ''}
                          </p>
                        </div>
                        {item.drive_link && (
                          <a href={item.drive_link} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-brand-700 hover:underline">
                            Open
                          </a>
                        )}
                      </div>
                    ))}
                    {skipped.map((s) => (
                      <div key={`skip-${room.id}-${s.name}`} className="px-3 py-1.5 flex items-start gap-2 text-xs text-amber-800 bg-amber-50/60">
                        <FileWarning className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span><b>{s.name}</b>: {s.reason}</span>
                      </div>
                    ))}
                    {errors.map((e) => (
                      <div key={`err-${room.id}-${e.name}`} className="px-3 py-1.5 flex items-start gap-2 text-xs text-rose-800 bg-rose-50/60">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span><b>{e.name}</b>: {e.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <p className="text-[11px] text-slate-400">
            Caply reads the room with your firm&apos;s connected Google account and copies files into the
            deal&apos;s Drive folder. Login-walled rooms record the link only.
          </p>
        </div>
      )}
    </div>
  );
}
